/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  GraphicalTakeOffBoxDto,
  GraphicalTakeOffPoint,
  GraphicalTakeOffTopDto,
  GraphicalTakeOffTopViewDto,
  GraphicalTakeOffTopViewInfoDto, GtoUom,
} from '@gh/builder-shared-data';
import { createArrayHelper, setEntityField, setEntityFields, setNestedEntityField } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { decimalToFoot, Enum, footToDecimal, Quantity, truncateFoot } from '@gh/core-data';
import { isNotNil } from '@gh/core-util';
import { findLast, first, fromPairs, identity, initial, isEmpty, isEqual, isNil, keys, last, map, mapValues, omit, toPairs, uniqueId, without } from 'lodash';
import { Path, Point } from 'paper';
import { toPaperPoint } from '../../components/canvas/canvas.utils';
import { graphicalTakeOffCurrentView, GraphicalTakeOffDetails } from './graphical-take-off.model';

export const GTO_MIN_MEASUREMENT_POINT_DISTANCE = 5;
export const GTO_SNAP_GRID_MAX_DISTANCE = 15;

export type GraphicalTakeOffMeta = {
  isChanged: boolean;
  type: string;
  version?: number;
  measures: GraphicalTakeOffMeasurement[];
};
export type GraphicalTakeOffTop = GraphicalTakeOffTopDto;
export type GraphicalTakeOffTopViewInfo = GraphicalTakeOffTopViewInfoDto;
export type GraphicalTakeOffTopView = {
  id?: ID;
  hpViewId?: ID;
  jobViewId?: ID;
  jobTakeOffParamId?: ID;
  takeOffParam: GraphicalTakeOffTopViewInfo;
  metadata: GraphicalTakeOffMeta;
  appendMode: boolean;
};
export type GraphicalTakeOffMeasurement = {
  id: ID;
  isChanged: boolean;
  value?: number;
  mainDatabox?: GraphicalTakeOffBox;
  points: GraphicalTakeOffPoint[];
  databoxes?: GraphicalTakeOffBox[];
  drawingMode: boolean;
  draftMode: boolean;
};

export type GraphicalTakeOffBox = GraphicalTakeOffBoxDto & { id: ID };
export type GraphicalTakeOffBoxDetails = {
  id: ID;
  point: GraphicalTakeOffPoint;
  label: string;
  value?: Quantity<any>;
  originalValue?: number;
};

export type GraphicalTakeOffTopCurrentSummary = {
  allViews: number;
  byViews: Hash<number>;
  selection: number;
  isChanged: boolean;
};
export type GraphicalTakeOffTopPersistedSummary = {
  allViews: number;
  byViews: Hash<number>;
};

export const getGraphicalTakeOffTopId = (top: GraphicalTakeOffTop) => top.takeOffParamId;
export const getGraphicalTakeOffTopViewId = (top: GraphicalTakeOffTopView) => top.takeOffParam.takeOffParamId;
export const getGraphicalTakeOffMeasurementId = (measurement: GraphicalTakeOffMeasurement) => measurement.id;
export const getGraphicalTakeOffBoxId = (dataBox: GraphicalTakeOffBox) => dataBox.id;

export const graphicalTakeOffTopArr = createArrayHelper(getGraphicalTakeOffTopId);
export const graphicalTakeOffTopViewArr = createArrayHelper(getGraphicalTakeOffTopViewId);
export const graphicalTakeOffMeasurementArr = createArrayHelper(getGraphicalTakeOffMeasurementId);
export const graphicalTakeOffBoxArr = createArrayHelper(getGraphicalTakeOffBoxId);

export enum GraphicalTakeOffUom {
  Millimeters = 'MM',
  Meters = 'M',
  MetersSquare = 'M2',
  Feets = 'FT',
  FeetsSquare = 'FT2',
}

export type GraphicalTakeOffMeasurementDataboxConfig = {
  point: Point;
};

export const GTO_MEASUREMENT_UNITS = {
  LENGTH: [GraphicalTakeOffUom.Meters, GraphicalTakeOffUom.Millimeters, GraphicalTakeOffUom.Feets],
  AREA: [GraphicalTakeOffUom.MetersSquare, GraphicalTakeOffUom.FeetsSquare],
  COUNT: ['COUNT'],
};

// tslint:disable-next-line:max-line-length
export function isGraphicalTakeOffTopView(top: GraphicalTakeOffTop | GraphicalTakeOffTopView): top is GraphicalTakeOffTopView {
  return isNotNil(top['metadata']);
}

export function getGraphicalTakeOffTopUom(top: GraphicalTakeOffTop | GraphicalTakeOffTopView): GtoUom {
  const uom = isGraphicalTakeOffTopView(top) ? top.takeOffParam.gtoUom : top.gtoUom;
  return Enum.byValue(GtoUom, uom.toUpperCase());
}

export function getGraphicalTakeOffTopType(top: GraphicalTakeOffTop | GraphicalTakeOffTopView): string {
  let gtoUom: string;

  if (isGraphicalTakeOffTopView(top)) {
    const { metadata, takeOffParam } = top;
    if (metadata.type) {
      return metadata.type;
    }
    gtoUom = takeOffParam.gtoUom;
  } else {
    gtoUom = top.gtoUom || '';
  }

  gtoUom = gtoUom.toUpperCase();

  if (gtoUom) {
    const type = Object
      .keys(GTO_MEASUREMENT_UNITS)
      .find((key) => -1 !== GTO_MEASUREMENT_UNITS[key].indexOf(gtoUom));

    if (type) {
      return type;
    }
  }

  return '';
}

export const isGraphicalTakeOffCountType = (type: string) => type === 'COUNT';
export const isGraphicalTakeOffLengthType = (type: string) => type === 'LENGTH';
export const isGraphicalTakeOffAreaType = (type: string) => type === 'AREA';

export const GTO_MEASUREMENT_DEFAULT_COLOR: number = 16777215;

export function getGraphicalTakeOffStyle(top: GraphicalTakeOffTop | GraphicalTakeOffTopView): number {
  if (isGraphicalTakeOffTopView(top)) {
    return top.takeOffParam.gtoStyle;
  } else {
    return top.gtoStyle || 0;
  }
}

export function getGraphicalTakeOffColor(top: GraphicalTakeOffTop | GraphicalTakeOffTopView): number {
  if (isGraphicalTakeOffTopView(top)) {
    return top.takeOffParam.gtoColor;
  } else {
    return top.gtoColor || GTO_MEASUREMENT_DEFAULT_COLOR;
  }
}

export const isGraphicalTakeOffTopViewPersisted = (top: GraphicalTakeOffTopView) => isNotNil(top.id);

export const isGraphicalTakeOffMeasurementCompleted = (top: GraphicalTakeOffTopView,
                                                       measurement: GraphicalTakeOffMeasurement) => {
  // drawing is not started
  return !(top.appendMode || measurement.drawingMode);
};

export const isGraphicalTakeOffMeasurementValid = (top: GraphicalTakeOffTopView,
                                                   measurement: GraphicalTakeOffMeasurement) => {

  switch (getGraphicalTakeOffTopType(top)) {
    case 'COUNT':
      return true;
    case 'LENGTH':
      return measurement ? measurement.points.length >= 2 : false;
    case 'AREA':
      if (measurement && measurement.points.length >= 3) {
        const firstPoint = first(measurement.points);
        const lastPoint = last(measurement.points);
        return isEqual(firstPoint, lastPoint);
      } else {
        return false;
      }
    default:
      return false;
  }
};

export const isGraphicalTakeOffMeasurementCanSnapToStart = (top: GraphicalTakeOffTopView,
                                                            measurement: GraphicalTakeOffMeasurement) => {

  switch (getGraphicalTakeOffTopType(top)) {
    case 'COUNT':
      return false;
    case 'LENGTH':
      return measurement ? measurement.points.length >= 3 : false;
    case 'AREA':
      return measurement && measurement.points.length >= 3;
    default:
      return false;
  }
};

export const isGraphicalTakeOffMeasurementClosed = ({ points }: GraphicalTakeOffMeasurement) =>
  points.length > 1 && isEqual(first(points), last(points));

export const isGraphicalTakeOffTopViewCompleted = (top: GraphicalTakeOffTopView,
                                                   selection: GraphicalTakeOffMeasurement) => {
  // if selection does not exist => top is considered as completed
  return isNotNil(selection) ? isGraphicalTakeOffMeasurementCompleted(top, selection) : top.appendMode;
};

export const isGraphicalTakeOffTopViewValid = (top: GraphicalTakeOffTopView,
                                               selection: GraphicalTakeOffMeasurement) => {
  if (isGraphicalTakeOffTopViewCompleted(top, selection)) {
    return true;
  }
  // if selection does not exist => top is considered as completed
  return isNotNil(selection) ? isGraphicalTakeOffMeasurementValid(top, selection) : top.appendMode;
};

export const isGraphicalTakeOffTopViewCanSnapToStart = (top: GraphicalTakeOffTopView,
                                                        selection: GraphicalTakeOffMeasurement) => {
  if (isGraphicalTakeOffTopViewCompleted(top, selection)) {
    return false;
  }
  // if selection does not exist => top is considered as completed
  return isNotNil(selection) ? isGraphicalTakeOffMeasurementCanSnapToStart(top, selection) : false;
};

export const graphicalTakeOffTopViewFromDto = (top: GraphicalTakeOffTopViewDto): GraphicalTakeOffTopView => ({
  ...top,
  metadata: {
    ...top.metadata,
    measures: top.metadata.measures.map((measure) => ({
      ...measure,
      id: uniqueId('measure:'),
      databoxes: map(measure.databoxes, graphicalTakeOffTopDataBoxFromDto),
      mainDatabox: measure.mainDatabox && graphicalTakeOffTopDataBoxFromDto(measure.mainDatabox),
      // somehow sometimes array of points has nulls
      points: measure.points.filter(Boolean),
      drawingMode: false,
      draftMode: false,
    })),
  },
  appendMode: false,
});

export const graphicalTakeOffTopDataBoxFromDto = (databox: GraphicalTakeOffBoxDto): GraphicalTakeOffBox => ({
  ...databox,
  id: uniqueId('databox:'),
});

export const graphicalTakeOffTopDataBoxToDto = (databox: GraphicalTakeOffBox): GraphicalTakeOffBoxDto =>
  omit(databox, 'id');

export const createDatabox = (point: GraphicalTakeOffPoint,
                              value: number,
                              dataBoxLabel: string): GraphicalTakeOffBox => ({
  id: uniqueId('databox:'),
  point,
  value,
  originalValue: value,
  label: dataBoxLabel,
});

export const setDataboxOriginalValue = (databox: GraphicalTakeOffBox, value: number) =>
  databox.value !== databox.originalValue ?
    setEntityField(databox, 'originalValue', value)
    : setEntityFields(databox, { originalValue: value, value });

export const createMeasurement = (): GraphicalTakeOffMeasurement => ({
  id: uniqueId('measure:'),
  databoxes: [],
  points: [],
  value: 1,
  isChanged: false,
  drawingMode: true,
  draftMode: true,
});

export const createCountMeasurement = (point: GraphicalTakeOffPoint,
                                       dataBoxLabel: string): GraphicalTakeOffMeasurement =>
  setEntityFields(createMeasurement(), {
    points: [point],
    mainDatabox: createDatabox(point, 1, dataBoxLabel),
    value: 1,
    drawingMode: false,
  });

export const setPointForCountMeasurement = (measurement: GraphicalTakeOffMeasurement, point: GraphicalTakeOffPoint) =>
  setEntityFields(measurement, {
    points: [point],
    mainDatabox: measurement.mainDatabox ? setEntityField(measurement.mainDatabox, 'point', point) : void 0,
  });

export const createSquaredMeasurement = (point: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement =>
  setEntityFields(createMeasurement(), {
    points: [point],
  });

export const createLengthMeasurement = createSquaredMeasurement;
export const createAreaMeasurement = createSquaredMeasurement;

export const addPointIntoLengthMeasurement = (details: GraphicalTakeOffDetails,
                                              topView: GraphicalTakeOffTopView,
                                              measurement: GraphicalTakeOffMeasurement,
                                              nextPoint: GraphicalTakeOffPoint,
                                              dataBoxLabel: string): GraphicalTakeOffMeasurement => {
  return setPointForLengthMeasurement(
    details,
    topView,
    setEntityFields(measurement, {
      points: measurement.points.concat([nextPoint]),
      databoxes: (measurement.databoxes || []).concat([createDatabox({ x: 0, y: 0 }, 1, dataBoxLabel)]),
      draftMode: true,
    }),
    nextPoint);
};

export const setPointForLengthMeasurement = (details: GraphicalTakeOffDetails,
                                             topView: GraphicalTakeOffTopView,
                                             measurement: GraphicalTakeOffMeasurement,
                                             point: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement =>
  measurement.points.length === 1 ?
    setFirstPointForLengthMeasurement(measurement, point)
    : setNextPointForLengthMeasurement(details, topView, measurement, point);

export const setFirstPointForLengthMeasurement = (measurement: GraphicalTakeOffMeasurement,
                                                  point: GraphicalTakeOffPoint) =>
  setEntityField(measurement, 'points', [point]);

export const setNextPointForLengthMeasurement = (details: GraphicalTakeOffDetails,
                                                 topView: GraphicalTakeOffTopView,
                                                 measurement: GraphicalTakeOffMeasurement,
                                                 point: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement => {
  const { picture, measurementDefault } = graphicalTakeOffCurrentView(details);

  const previousPoint = measurement.points[measurement.points.length - 2]; // tslint:disable-line:no-non-null-assertion
  const value = truncateValueByUom(
    convertValueUom(
      measurementDefault,
      topView.takeOffParam.gtoUom,
      pointLength(scalePoint(diffPoints(point, previousPoint), picture.scaleWidth || 0, picture.scaleHeight || 0))),
    topView.takeOffParam.gtoUom);
  const points = (measurement.points || []);
  const databoxes = (measurement.databoxes || []);

  return setEntityFields(measurement, {
    points: points.slice(0, points.length - 1).concat([point]),
    databoxes: databoxes.slice(0, databoxes.length - 1).concat([
      setDataboxOriginalValue(
        setEntityField(databoxes[databoxes.length - 1], 'point', middlePoint(previousPoint, point)),
        value),
    ]),
  });
};

export const addPointIntoAreaMeasurement = (details: GraphicalTakeOffDetails,
                                            topView: GraphicalTakeOffTopView,
                                            measurement: GraphicalTakeOffMeasurement,
                                            nextPoint: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement => {
  return setPointForAreaMeasurement(
    details,
    topView,
    setEntityFields(measurement, {
      points: measurement.points.concat([nextPoint]),
      draftMode: true,
    }),
    nextPoint);
};
export const setPointForAreaMeasurement = (details: GraphicalTakeOffDetails,
                                           topView: GraphicalTakeOffTopView,
                                           measurement: GraphicalTakeOffMeasurement,
                                           point: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement =>
  measurement.points.length === 1 ?
    setFirstPointForAreaMeasurement(measurement, point)
    : setNextPointForAreaMeasurement(details, topView, measurement, point);

export const setFirstPointForAreaMeasurement = (measurement: GraphicalTakeOffMeasurement,
                                                point: GraphicalTakeOffPoint) =>
  setEntityField(measurement, 'points', [point]);

export const setNextPointForAreaMeasurement = (details: GraphicalTakeOffDetails,
                                               topView: GraphicalTakeOffTopView,
                                               measurement: GraphicalTakeOffMeasurement,
                                               point: GraphicalTakeOffPoint): GraphicalTakeOffMeasurement => {
  const points = measurement.points || [];

  return setEntityFields(measurement, {
    points: points.slice(0, points.length - 1).concat([point]),
  });
};

export const commitPointForTop = (top: GraphicalTakeOffTopView) => {
  const lastMeasurement = last(top.metadata.measures)!; // tslint:disable-line:no-non-null-assertion
  return setNestedEntityField(
    top,
    ['metadata', 'measures'],
    graphicalTakeOffMeasurementArr.update(
      top.metadata.measures,
      getGraphicalTakeOffMeasurementId(lastMeasurement),
      commitPointForMeasurement(lastMeasurement)));
};

export const rollbackPointForTop = (top: GraphicalTakeOffTopView) => {
  const lastMeasurement = last(top.metadata.measures)!; // tslint:disable-line:no-non-null-assertion
  return setNestedEntityField(
    top,
    ['metadata', 'measures'],
    graphicalTakeOffMeasurementArr.update(
      top.metadata.measures,
      getGraphicalTakeOffMeasurementId(lastMeasurement),
      rollbackPointForMeasurement(lastMeasurement)));
};

export const commitPointForMeasurement = (measurement: GraphicalTakeOffMeasurement) =>
  setEntityField(measurement, 'draftMode', false);

export const rollbackPointForMeasurement = (measurement: GraphicalTakeOffMeasurement) =>
  setEntityFields(measurement, {
    draftMode: false,
    points: initial(measurement.points),
    databoxes: initial(measurement.databoxes),
  });

export const removeMeasurementPoint = (topView: GraphicalTakeOffTopView,
                                       measurement: GraphicalTakeOffMeasurement) => {
  switch (getGraphicalTakeOffTopType(topView)) {
    case 'COUNT':
      return setEntityField(measurement, 'points', initial(measurement.points));
    case 'LENGTH':
      return setEntityFields(measurement, {
        points: initial(measurement.points),
        mainDatabox: void 0,
        databoxes: initial(measurement.databoxes),
        drawingMode: true,
      });
    case 'AREA':
      return setEntityFields(measurement, {
        points: initial(measurement.points),
        mainDatabox: void 0,
        drawingMode: true,
      });
    default:
      return measurement;
  }
};

export const newPoint = (x: number, y: number) => ({ x, y });
export const mulPoint = (point: GraphicalTakeOffPoint, c: number) => ({ x: point.x * c, y: point.y * c });
export const shiftPoint = (point: GraphicalTakeOffPoint, c: number) => ({ x: point.x + c, y: point.y + c });
export const addPoint = (a: GraphicalTakeOffPoint, b: GraphicalTakeOffPoint) => ({ x: a.x + b.x, y: b.y + a.y });
export const diffPoints = (a: GraphicalTakeOffPoint, b: GraphicalTakeOffPoint) => ({ x: a.x - b.x, y: a.y - b.y });
export const scalePoint = ({ x, y }: GraphicalTakeOffPoint, scaleX: number, scaleY: number = scaleX) =>
  ({ x: x * scaleX, y: y * scaleY });
export const pointLength = ({ x, y }: GraphicalTakeOffPoint) => Math.sqrt(x * x + y * y);

export const middlePoint = (...points: GraphicalTakeOffPoint[]) =>
  mulPoint(
    points.reduce(
      (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
      { x: 0, y: 0 }),
    1 / points.length);

export const newSize = (width: number, height: number) => ({ width, height });
export const minPoint = (point1: GraphicalTakeOffPoint, point2: GraphicalTakeOffPoint) =>
  newPoint(Math.min(point1.x, point2.x), Math.min(point1.y, point2.y));
export const maxPoint = (point1: GraphicalTakeOffPoint, point2: GraphicalTakeOffPoint) =>
  newPoint(Math.max(point1.x, point2.x), Math.max(point1.y, point2.y));

export const getGraphicalTakeOffTopViewLastMeasurement = (top: GraphicalTakeOffTopView) =>
  last(top.metadata.measures);
export const getGraphicalTakeOffTopViewAllMeasurements = (top: GraphicalTakeOffTopView) => top.metadata.measures;

export const isMeasurementChanged = (topView: GraphicalTakeOffTopView, measurement: GraphicalTakeOffMeasurement) => {
  const type = getGraphicalTakeOffTopType(topView);
  if (isGraphicalTakeOffCountType(type)) {
    if (measurement.mainDatabox) {
      return isDataBoxValueChanged(measurement.mainDatabox);
    } else {
      return false;
    }
  } else if (isGraphicalTakeOffLengthType(type)) {
    return (measurement.mainDatabox ? isDataBoxValueChanged(measurement.mainDatabox) : false)
      || (measurement.databoxes ? measurement.databoxes.some(isDataBoxValueChanged) : false);
  } else if (isGraphicalTakeOffAreaType(type)) {
    if (measurement.mainDatabox) {
      return isDataBoxValueChanged(measurement.mainDatabox);
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const isDataBoxValueChanged = (dataBox: GraphicalTakeOffBox) =>
  isNotNil(dataBox.originalValue) && dataBox.originalValue !== dataBox.value;

export const getDataBoxValue = (dataBox: GraphicalTakeOffBox) => dataBox.value || 0;

export const reduceTopViewMeasurements = <T>(topView: GraphicalTakeOffTopView,
                                             measurementReduce: (acc: T, dataBox: GraphicalTakeOffMeasurement) => T,
                                             initialValue: T): T =>
  topView.metadata.measures.reduce(measurementReduce, initialValue);

export const consistencyMeasurementTransform = (topView: GraphicalTakeOffTopView,
                                                measurement: GraphicalTakeOffMeasurement) => {
  const isChanged = isMeasurementChanged(topView, measurement);
  if (measurement.isChanged !== isChanged) {
    return mergeMeasurementSumValue(topView, { ...measurement, isChanged });
  } else {
    return mergeMeasurementSumValue(topView, measurement);
  }
};

const mergeMeasurementSumValue = (topView: GraphicalTakeOffTopView, measurement: GraphicalTakeOffMeasurement) => {
  const originalValue = calculateMeasurementOriginalValue(topView, measurement);
  const value = calculateMeasurementValue(topView, measurement);
  return setEntityField(
    measurement.mainDatabox ?
      setEntityField(measurement, 'mainDatabox', setDataboxOriginalValue(measurement.mainDatabox, originalValue))
      : measurement,
    'value',
    value);
};

const calculateMeasurementOriginalValue = (topView: GraphicalTakeOffTopView,
                                           measurement: GraphicalTakeOffMeasurement) => {
  const type = getGraphicalTakeOffTopType(topView);
  if (isGraphicalTakeOffCountType(type)) {
    if (measurement.mainDatabox) {
      return getDataBoxOriginalValue(measurement.mainDatabox);
    } else {
      return 0;
    }
  } else if (isGraphicalTakeOffLengthType(type)) {
    if (measurement.databoxes) {
      return measurement.databoxes.reduce((sum, dataBox) => sum + getDataBoxValue(dataBox), 0);
    } else {
      return 0;
    }
  } else if (isGraphicalTakeOffAreaType(type)) {
    if (measurement.mainDatabox) {
      return getDataBoxValue(measurement.mainDatabox);
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

const calculateMeasurementValue = (topView: GraphicalTakeOffTopView, measurement: GraphicalTakeOffMeasurement) => {
  const type = getGraphicalTakeOffTopType(topView);
  if (isGraphicalTakeOffCountType(type)) {
    if (measurement.mainDatabox) {
      return getDataBoxValue(measurement.mainDatabox);
    } else {
      return 0;
    }
  } else if (isGraphicalTakeOffLengthType(type)) {
    if (measurement.mainDatabox && isDataBoxValueChanged(measurement.mainDatabox)) {
      return getDataBoxValue(measurement.mainDatabox);
    } else if (measurement.databoxes) {
      return measurement.databoxes.reduce((sum, dataBox) => sum + getDataBoxValue(dataBox), 0);
    } else {
      return 0;
    }
  } else if (isGraphicalTakeOffAreaType(type)) {
    if (measurement.mainDatabox) {
      return getDataBoxValue(measurement.mainDatabox);
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

export const consistencyTopViewTransform = (topView: GraphicalTakeOffTopView) => {
  const topViewWithConsistentMeasurements = setNestedEntityField(
    topView,
    ['metadata', 'measures'],
    topView.metadata.measures.map((measurement) => consistencyMeasurementTransform(topView, measurement)));
  const isChanged = reduceTopViewMeasurements(
    topViewWithConsistentMeasurements,
    (acc, measurement) => acc || measurement.isChanged,
    false);
  if (topViewWithConsistentMeasurements.metadata.isChanged !== isChanged) {
    return {
      ...topViewWithConsistentMeasurements,
      metadata: {
        ...topViewWithConsistentMeasurements.metadata,
        isChanged,
      },
    };
  } else {
    return topViewWithConsistentMeasurements;
  }
};

export const graphicalTakeOffTopViewValue = (topView: GraphicalTakeOffTopView) =>
  topView.metadata.measures.reduce((value, measure) => value + (measure.value || 0), 0);

export const graphicalTakeOffTopViewCurrentSelectionValue = (topView: GraphicalTakeOffTopView) => {
  const lastCompletedMeasurement = findLast(
    topView.metadata.measures,
    (measurement) => isGraphicalTakeOffMeasurementCompleted(topView, measurement));
  return lastCompletedMeasurement && lastCompletedMeasurement.value;
};

export const isGrpahicalTakeOffTopViewMeasurementExists = (topView: GraphicalTakeOffTopView) =>
  !isEmpty(topView.metadata.measures);

export const canDrawMeasurement = (topView: GraphicalTakeOffTopView) => {
  const type = getGraphicalTakeOffTopType(topView);

  if (isGraphicalTakeOffCountType(type)) {
    return true;
  }

  if (shouldAppendMeasurement(topView)) {
    return true;
  } else {
    const lastMeasurement = last(topView.metadata.measures)!; // tslint:disable-line:no-non-null-assertion
    return lastMeasurement.drawingMode;
  }
};

export const shouldAppendMeasurement = (topView: GraphicalTakeOffTopView) =>
  topView.metadata.measures.length === 0 || topView.appendMode;

export const GTO_MEASUREMENT_UNITS_MMFT = 0.00328084;
export const GTO_MEASUREMENT_UNITS_MFT = 3.2808;
export const GTO_MEASUREMENT_UNITS_MMM = 1000;
export const GTO_MEASUREMENT_UNITS_IN_FT = 12;

// ratios of meter (default uom) to feets and millimeters (derived uoms)
const DEFAULT_UOM = 'M';
const DEFAULT_UOM_2 = `${DEFAULT_UOM}2`;
const DEFAULT_RATIOS: Hash<number> = {
  FT: GTO_MEASUREMENT_UNITS_MFT,
  MM: GTO_MEASUREMENT_UNITS_MMM,
};
const DEFAULT_RATIOS_2: Hash<number> = fromPairs(toPairs(DEFAULT_RATIOS).map(([uom, ratio]) => [
  `${uom}2`,
  ratio * ratio,
]));

const buildFullRatioMap = (defaultUom: string, defaultRatios: Hash<number>) => {
  const allUoms = [defaultUom].concat(keys(defaultRatios));
  return fromPairs(allUoms.map((sourceUom) => {
    if (sourceUom === defaultUom) {
      return [sourceUom, defaultRatios];
    } else {
      const sourceUomViaDefaultUom = 1 / defaultRatios[sourceUom];
      return [
        sourceUom,
        fromPairs(without(allUoms, sourceUom).map((targetUom) => [
          targetUom,
          targetUom === defaultUom ? sourceUomViaDefaultUom : (sourceUomViaDefaultUom * defaultRatios[targetUom]),
        ])),
      ];
    }
  }));
};

const buildConverterMap = (): Hash<Hash<IdentityFn<number>>> => {
  const lineRatioMap = buildFullRatioMap(DEFAULT_UOM, DEFAULT_RATIOS);
  const squaredRatioMap = buildFullRatioMap(DEFAULT_UOM_2, DEFAULT_RATIOS_2);

  const lineToSquareRatioMap = mapValues(lineRatioMap, (targetUomMap) => ({
    ...targetUomMap,
    ...fromPairs(toPairs(targetUomMap).map(([targetUom, ratio]: [string, number]) => [`${targetUom}2`, ratio * ratio])),
  }));

  const ratioMap = {
    ...lineToSquareRatioMap,
    ...squaredRatioMap,
  };

  return mapValues(ratioMap, (ratios) => mapValues(ratios, (ratio) => (v: number) => v * ratio));
};

const UOM_CONVERTERS = buildConverterMap();

export const convertValueUom = (sourceUom: string, targetUom: string, value: number) => {
  const converter = UOM_CONVERTERS[sourceUom.toUpperCase()][targetUom.toUpperCase()] || identity;
  return converter(value);
};

const UOM_FRACTIONAL_2_TRUNCATOR = (value: number) => Math.round(value * 100) / 100;
const UOM_FT_TRUNCATOR = (value: number) => footToDecimal(truncateFoot(decimalToFoot(value)));

const UOM_TRUNCATORS = {
  FT: UOM_FT_TRUNCATOR,
  M: UOM_FRACTIONAL_2_TRUNCATOR,
  MM: UOM_FRACTIONAL_2_TRUNCATOR,
  FT2: UOM_FRACTIONAL_2_TRUNCATOR,
  M2: UOM_FRACTIONAL_2_TRUNCATOR,
  MM2: UOM_FRACTIONAL_2_TRUNCATOR,
  COUNT: identity,
};

export const truncateValueByUom = (value: number, uom: string): number => UOM_TRUNCATORS[uom.toUpperCase()](value);

export const truncatePointByUom = (point: GraphicalTakeOffPoint, uom: string): GraphicalTakeOffPoint =>
  newPoint(truncateValueByUom(point.x, uom), truncateValueByUom(point.y, uom));

export const convertDataboxUom = (sourceUom: string,
                                  targetUom: string,
                                  dataBox: GraphicalTakeOffBox) =>
  setDataboxOriginalValue(
    dataBox,
    truncateValueByUom(convertValueUom(sourceUom, targetUom, dataBox.value || 0), targetUom));

export const convertMeasurementUom = (sourceUom: string,
                                      targetUom: string,
                                      measurement: GraphicalTakeOffMeasurement) =>
  setEntityFields(measurement, {
    mainDatabox: measurement.mainDatabox ? convertDataboxUom(sourceUom, targetUom, measurement.mainDatabox) : void 0,
    databoxes: (measurement.databoxes || []).map((dataBox) => convertDataboxUom(sourceUom, targetUom, dataBox)),
  });

export const isValidToClone = (measurement: GraphicalTakeOffMeasurement, targetTop: GraphicalTakeOffTop) => {
  const targetType = getGraphicalTakeOffTopType(targetTop);
  switch (targetType) {
    case 'LENGTH':
      return true;
    case 'AREA': {
      return isValidArea(normalizePointsForClone(measurement.points));
    }
    default:
      return false;
  }
};

export const normalizePointsForClone = (points: GraphicalTakeOffPoint[]) =>
  points.length > 2 && isEqual(first(points), last(points)) ? points : points.concat([points[0]]);

export const isValidArea = (points: GraphicalTakeOffPoint[]) => {
  const path = new Path(points.map(toPaperPoint));
  const intersections = path.getIntersections(path);
  path.remove();
  return intersections.length <= 1;
};

export const calculateArea = (points: GraphicalTakeOffPoint[]) => {
  const path = new Path(points.map(toPaperPoint));
  const area = Math.abs(path.area);
  path.remove();
  return area;
};

export const calculateMinDistanceToPath = (points: GraphicalTakeOffPoint[], point: GraphicalTakeOffPoint) => {
  const path = new Path(points.map(toPaperPoint));
  const lastPaperPoint = toPaperPoint(point);
  const nearestPathPoint = path.getNearestPoint(lastPaperPoint);
  const distance = lastPaperPoint.getDistance(nearestPathPoint);
  path.remove();
  return distance;
};

export const getPathIntersectionCount = (pointsA: GraphicalTakeOffPoint[], pointsB: GraphicalTakeOffPoint[]) => {
  const pathA = new Path(pointsA.map(toPaperPoint));
  const pathB = new Path(pointsB.map(toPaperPoint));
  const intersectionCount = pathA.getIntersections(pathB).length;
  pathA.remove();
  pathB.remove();
  return intersectionCount;
};

export const findClosestPoint = (points: GraphicalTakeOffPoint[], testPoint: GraphicalTakeOffPoint) => {
  if (points.length === 0) {
    return;
  }

  const { point: foundPoint } = points.reduce(
    (result, corner) => {
      const length = pointLength(diffPoints(corner, testPoint));
      return length < result.length ? { length, point: corner } : result;
    },
    { length: Number.MAX_SAFE_INTEGER, point: void 0 });

  return foundPoint;
};

export const findClosestPointOnDistance = (points: GraphicalTakeOffPoint[],
                                           testPoint: GraphicalTakeOffPoint,
                                           maxDistance: number) => {
  if (points.length === 0) {
    return;
  }

  const { point: foundPoint } = points.reduce(
    (result, corner) => {
      const length = pointLength(diffPoints(corner, testPoint));
      return length <= maxDistance && length < result.length ? { length, point: corner } : result;
    },
    { length: Number.MAX_SAFE_INTEGER, point: void 0 });

  return foundPoint;
};

export const getDataBoxOriginalValue = (dataBox: GraphicalTakeOffBox) => dataBox.originalValue || dataBox.value || 0;

export const scaleArea = (uom: string, value: number, scaleWidthRatio: number, scaleHeightRatio: number) =>
  truncateValueByUom(value * scaleWidthRatio * scaleHeightRatio, uom);

export const scaleLength = (uom: string,
                            value: number,
                            pStart: GraphicalTakeOffPoint,
                            pEnd: GraphicalTakeOffPoint,
                            scaleWidthRatio: number,
                            scaleHeightRatio: number) => {
  // calculate direction vector
  const pVector = diffPoints(pEnd, pStart);
  // calculate unit vector in projective space based on length direction
  const pUnitVector = mulPoint(pVector, 1 / pointLength(pVector));
  // calculate vector in source scaled space
  const sourceScaledVector = mulPoint(pUnitVector, value);
  // calculate length of scaled vector in target scaled space
  return truncateValueByUom(
    pointLength(newPoint(
      sourceScaledVector.x * scaleWidthRatio,
      sourceScaledVector.y * scaleHeightRatio)),
    uom);
};

export const isGraphicalTakeOffTopViewNonMigrated = (topView: GraphicalTakeOffTopView) =>
  isNil(topView.metadata.version);

export const appendGtoTopViewMeasurements = (topView: GraphicalTakeOffTopView,
                                             sourceUom: string,
                                             measurements: GraphicalTakeOffMeasurement[]): GraphicalTakeOffTopView => {
  const isCount = isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(topView));

  // convert UoM if it is not a count ToP
  const sourceMeasurements = isCount ?
    measurements
    : measurements.map((measurement) => convertMeasurementUom(sourceUom, topView.takeOffParam.gtoUom, measurement));

  return setNestedEntityField(
    topView,
    ['metadata', 'measures'],
    graphicalTakeOffMeasurementArr.append(topView.metadata.measures, ...sourceMeasurements));
};

export const removeGtoTopViewMeasurements = (topView: GraphicalTakeOffTopView,
                                             measurements: GraphicalTakeOffMeasurement[]): GraphicalTakeOffTopView => {
  const { measures: existingMeasurements } = topView.metadata;
  return setNestedEntityField(
    topView,
    ['metadata', 'measures'],
    without(existingMeasurements, ...measurements));
};
